import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import Select, {
  SelectInput,
  type ClassNamesConfig,
  type ClearIndicatorProps,
  type FormatOptionLabelMeta,
  type SelectInputProps,
  type StylesConfig
} from '@databases/atlaskit/Select';
import { PageStatusColorPicker } from '@databases/color-picker';
import PageStatus from '@databases/field-value/pageStatus/PageStatus';
import { messages } from '@databases/intl/messages';
import { type Optional } from '@databases/types';
import { type ContentState } from '@databases/types/confluence';
import { getContentStateKey } from '@databases/utils/confluence/contentState';
import { DEFAULT_PAGE_STATUS_COLOR } from '@databases/utils/database/field-types/pageStatus';
import React, { type FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

import {
  baseSelectStyleConfig,
  customPlaceholderAndControlSpacingStyling
} from '../popover-edit-common/index.ts';
import styles from './page-status-select.module.css';

/** Max length which is allowed for a content state name */
const MAX_CONTENT_STATE_NAME_LENGTH = 20;

type Option = Optional<ContentState, 'id'>;

const ClearIndicator = (props: ClearIndicatorProps<Option>) => {
  const { children = <CrossIcon label="" />, innerProps } = props;

  return (
    <div {...innerProps} data-testid="page-status-picker-clear-indicator">
      {children}
    </div>
  );
};

const InputWithPlaceholder = (props: SelectInputProps<Option>) => {
  const hasValue = props.getValue().length > 0;

  return (
    <SelectInput
      {...props}
      isHidden={hasValue && props.isHidden}
      placeholder={props['aria-label']}
    />
  );
};

const HiddenPlaceholder = () => null;

const pageStatusSelectStyleConfig: StylesConfig<Option> = {
  ...baseSelectStyleConfig,
  ...customPlaceholderAndControlSpacingStyling
};

const pageStatusSelectClassNames: ClassNamesConfig<Option> = {
  input: ({ hasValue }) =>
    `${styles.input} ${hasValue ? styles.inputWithValue : styles.inputWithoutValue}`
};

const statusWrapperStyles = xcss({
  flexGrow: 1
});

const getOptionValue = (op: Option) => getContentStateKey(op);
const getOptionLabel = (op: Option) => op.name;

export interface PageStatusSelectProps {
  customOptions: ReadonlyArray<Option>;
  spaceOptions: ReadonlyArray<Option>;
  value?: Option | undefined;
  onChange?: (value: Option | undefined) => void;
  isCreatable?: boolean;
  onCreate?: (option: Option) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  noOptionsMessage?: string;
  initialKey?: string;
}

const PageStatusSelect: FunctionComponent<PageStatusSelectProps> = ({
  customOptions,
  spaceOptions,
  value = Object.freeze([]) as never[],
  onChange,
  isCreatable = false,
  onCreate,
  onFocus,
  onBlur,
  autoFocus,
  placeholder,
  isLoading,
  noOptionsMessage,
  initialKey
}) => {
  const [nextColor, setNextColor] = useState<string>(DEFAULT_PAGE_STATUS_COLOR);

  const [inputValue, setInputValue] = useState(initialKey ?? '');

  const { formatMessage } = useIntl();
  const handleInputChange = useCallback((val: string) => {
    setInputValue(val);
  }, []);

  const createOption = useMemo(() => {
    if (isCreatable) {
      const trimmed = inputValue.trim();
      const trimmedLower = inputValue.trim().toLowerCase();
      if (
        trimmed &&
        trimmed.length <= MAX_CONTENT_STATE_NAME_LENGTH &&
        ![...customOptions, ...spaceOptions].some(
          (o) => o.name.toLowerCase() === trimmedLower
        )
      ) {
        return { name: trimmed, color: nextColor };
      }
    }
  }, [customOptions, inputValue, isCreatable, nextColor, spaceOptions]);

  const resolvedOptions = useMemo(
    () =>
      [
        {
          options: spaceOptions,
          label: formatMessage(
            messages['field-settings.type-page-status-picker-default-workflow-text']
          )
        },
        {
          options: [...(createOption ? [createOption] : []), ...customOptions],
          label: formatMessage(
            messages['field-settings.type-page-status-picker-custom-statuses-text']
          )
        }
      ].filter((g) => g.options.length > 0),
    [createOption, customOptions, formatMessage, spaceOptions]
  );

  const handleChange = useCallback(
    (val: ReadonlyArray<Option> | Option | null) => {
      const newValue: Option | undefined =
        (Array.isArray(val) ? val[0] : val) ?? undefined;
      if (onCreate && createOption && newValue === createOption) {
        onCreate(createOption);
      }

      onChange?.(newValue);
    },
    [createOption, onChange, onCreate]
  );

  const formatOptionLabel = useCallback(
    (option: Option, meta: FormatOptionLabelMeta<Option>) => {
      if (meta.context === 'menu' && option.id == null) {
        if (createOption && option === createOption) {
          return (
            <Flex alignItems="center">
              {formatMessage(
                messages['field-settings.type-page-status-picker-create-text']
              )}
              &nbsp;
              <Box xcss={statusWrapperStyles}>
                <PageStatus
                  hasBottomMargin={false}
                  status={{
                    text: option.name,
                    color: option.color
                  }}
                />
              </Box>
              <PageStatusColorPicker
                value={option.color}
                onChange={(color) => {
                  setNextColor(color);
                }}
                stopPropagation
                size={24}
              />
            </Flex>
          );
        } else {
          return (
            <Flex alignItems="center">
              <PageStatus
                hasBottomMargin={false}
                status={{
                  text: option.name,
                  color: option.color
                }}
              />
              &nbsp;
              {formatMessage(messages['field-settings.type-page-status-picker-new-text'])}
            </Flex>
          );
        }
      } else {
        return (
          <Flex alignItems="center">
            <PageStatus
              hasBottomMargin={false}
              status={{
                text: option.name,
                color: option.color
              }}
            />
          </Flex>
        );
      }
    },
    [createOption, formatMessage]
  );

  const resolvedNoOptionsMessage = useMemo(
    () => (noOptionsMessage ? () => noOptionsMessage : undefined),
    [noOptionsMessage]
  );
  const resolvedPlaceholder =
    placeholder ??
    formatMessage(
      messages[
        isCreatable || isLoading
          ? 'page-status-picker-popover.search-create-text'
          : 'select-dropdown.placeholder'
      ]
    );

  return (
    <Select<Option>
      options={resolvedOptions}
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      placeholder={resolvedPlaceholder}
      ariaLabel={resolvedPlaceholder}
      getOptionValue={getOptionValue}
      getOptionLabel={getOptionLabel}
      onFocus={onFocus}
      onBlur={onBlur}
      autoFocus={autoFocus}
      formatOptionLabel={formatOptionLabel}
      hideSelectedOptions={false}
      styles={pageStatusSelectStyleConfig}
      className={styles.pageStatusSelect}
      classNames={pageStatusSelectClassNames}
      isLoading={isLoading}
      noOptionsMessage={resolvedNoOptionsMessage}
      defaultMenuIsOpen={autoFocus}
      isClearable
      menuPortalTarget={null}
      components={{
        ClearIndicator,
        Input: InputWithPlaceholder,
        Placeholder: HiddenPlaceholder
      }}
      classNamePrefix="page-status-picker"
    />
  );
};

export default PageStatusSelect;
